import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import chinaCategories from '../../../data/chinaCategories';
import { getChinaCategories } from '../../../api/mercuryx';

const CHINA_FLAG = "🇨🇳";

function formatCount(n) {
    if (!n) return '';
    if (n >= 1000000) return (n / 1000000).toFixed(1).replace('.0', '') + 'M';
    if (n >= 1000) return Math.round(n / 1000) + 'K';
    return n.toString();
}

// Skeleton card for loading state
function SkeletonCard() {
    return (
        <div className="bg-white rounded-2xl overflow-hidden animate-pulse">
            <div className="w-full aspect-square bg-gray-100" />
            <div className="px-3 py-2">
                <div className="h-3 bg-gray-100 rounded w-3/4 mb-1.5" />
                <div className="h-3 bg-gray-100 rounded w-1/2" />
            </div>
        </div>
    );
}

// Product placeholder card
function ProductPlaceholder({ index }) {
    const titles = [
        'Өнім тапсырыс беруге дайын',
        'Жаппай сату бағасы',
        'Тікелей өндірушіден',
        'Сертификатталған тауар',
    ];
    return (
        <div className="bg-white rounded-2xl overflow-hidden border border-dashed border-gray-200">
            <div className="w-full aspect-square bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
                <span className="text-4xl opacity-30">{CHINA_FLAG}</span>
            </div>
            <div className="px-3 py-2">
                <p className="text-xs font-medium text-gray-400 m-0 line-clamp-2">
                    {titles[index % titles.length]}
                </p>
                <span className="inline-block mt-1 text-[10px] text-gray-300 border border-gray-200 px-2 py-0.5 rounded-full">
                    Жақында
                </span>
            </div>
        </div>
    );
}

function ChinaSection({ advertMode, setAdvertMode }) {
    const { i18n, t } = useTranslation();
    const lang = i18n.language;

    // breadcrumb: [{id, name}]
    const [breadcrumb, setBreadcrumb] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [imgErr, setImgErr] = useState({});

    const getName = (cat) => {
        const names = cat.names || {};
        if (lang === 'cn') return names.cn || names.ru || '';
        if (lang === 'kk' || lang === 'kz') return names.kk || names.ru || '';
        return names.ru || names.kk || '';
    };

    const currentLevel = breadcrumb.length; // 0=top, 1=subcategory, 2=products

    // Fetch subcategories when drill-down
    useEffect(() => {
        if (currentLevel === 0 || currentLevel >= 2) return;
        const parentId = breadcrumb[breadcrumb.length - 1].id;
        setLoading(true);
        getChinaCategories(parentId)
            .then(data => setSubcategories(data))
            .catch(() => setSubcategories([]))
            .finally(() => setLoading(false));
    }, [breadcrumb, currentLevel]);

    const handleTopCategoryClick = (cat) => {
        setBreadcrumb([{ id: cat.id, name: getName(cat) }]);
        setSubcategories([]);
    };

    const handleSubcategoryClick = (cat) => {
        setBreadcrumb(prev => [...prev, { id: cat.id, name: getName(cat) }]);
    };

    const handleBreadcrumbClick = (index) => {
        if (index === -1) {
            setBreadcrumb([]);
            setSubcategories([]);
        } else {
            setBreadcrumb(prev => prev.slice(0, index + 1));
            setSubcategories([]);
        }
    };

    const activeTopId = breadcrumb[0]?.id ?? null;
    const activeSubId = breadcrumb[1]?.id ?? null;

    // What to show in the right panel
    const isProductLevel = currentLevel === 2;
    const isSubcategoryLevel = currentLevel === 1;

    return (
        <div className="flex gap-6 mt-[50px] px-5 md:px-[60px] lg:px-[120px] xl:px-[240px] pb-[60px] w-full items-start">

            {/* ── Left panel ── */}
            <div className="hidden md:flex flex-col w-[220px] flex-shrink-0">
                <div className="bg-white rounded-2xl p-3 sticky top-[90px] max-h-[calc(100vh-120px)] overflow-y-auto">

                    {/* Mode tabs */}
                    <div className="flex flex-col bg-gray-100 rounded-xl p-1 mb-3 gap-1">
                        <button
                            onClick={() => setAdvertMode('regular')}
                            className={`w-full py-1.5 text-xs font-semibold rounded-lg transition-all ${advertMode === 'regular' ? 'bg-[#147F26] text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            {t('regularAds') || 'Жарнамалар'}
                        </button>
                        <button
                            onClick={() => setAdvertMode('dealer')}
                            className={`w-full py-1.5 text-xs font-semibold rounded-lg transition-all ${advertMode === 'dealer' ? 'bg-[#147F26] text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            {t('dealerAds') || 'Дилерлік'}
                        </button>
                        <button
                            onClick={() => setAdvertMode('china')}
                            className={`w-full py-1.5 text-xs font-semibold rounded-lg transition-all ${advertMode === 'china' ? 'bg-[#147F26] text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            {t('chinaAds') || 'Қытайдан'}
                        </button>
                    </div>

                    {/* Category list header */}
                    <div className="flex items-center gap-2 px-2 py-1.5 mb-1 border-b border-gray-100">
                        <span className="text-sm">{CHINA_FLAG}</span>
                        <span className="text-xs font-bold text-gray-700">Категориялар</span>
                    </div>

                    {/* All */}
                    <button
                        onClick={() => handleBreadcrumbClick(-1)}
                        className={`w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium mb-0.5 transition-colors ${
                            activeTopId === null ? 'bg-[#147F26] text-white' : 'text-gray-600 hover:bg-gray-50'
                        }`}
                    >
                        Барлығы
                    </button>

                    {/* Top categories */}
                    {chinaCategories.map(cat => (
                        <button
                            key={cat.id}
                            onClick={() => handleTopCategoryClick(cat)}
                            className={`w-full text-left px-2 py-1.5 rounded-lg text-xs transition-colors flex items-center justify-between gap-1 ${
                                activeTopId === cat.id
                                    ? 'bg-[#147F26] text-white font-semibold'
                                    : 'text-gray-600 hover:bg-gray-50'
                            }`}
                        >
                            <span className="truncate">{getName(cat)}</span>
                            {cat.products_count > 0 && (
                                <span className={`text-[10px] flex-shrink-0 ${activeTopId === cat.id ? 'text-white/70' : 'text-gray-400'}`}>
                                    {formatCount(cat.products_count)}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── Right panel ── */}
            <div className="flex-1 min-w-0">

                {/* Breadcrumb */}
                <div className="flex items-center gap-1.5 flex-wrap mb-4 text-xs text-gray-500">
                    <button
                        onClick={() => handleBreadcrumbClick(-1)}
                        className="hover:text-[#147F26] transition-colors flex items-center gap-1"
                    >
                        <span>{CHINA_FLAG}</span>
                        <span>Қытайдан өнімдер</span>
                    </button>
                    {breadcrumb.map((crumb, i) => (
                        <React.Fragment key={crumb.id}>
                            <svg className="w-3 h-3 text-gray-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <button
                                onClick={() => handleBreadcrumbClick(i)}
                                className={`truncate max-w-[140px] transition-colors ${
                                    i === breadcrumb.length - 1
                                        ? 'text-gray-900 font-semibold pointer-events-none'
                                        : 'hover:text-[#147F26]'
                                }`}
                            >
                                {crumb.name}
                            </button>
                        </React.Fragment>
                    ))}
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-gray-900 m-0 mb-4">
                    {breadcrumb.length > 0
                        ? breadcrumb[breadcrumb.length - 1].name
                        : 'Барлық категориялар'}
                </h3>

                {/* Warning notice — always visible */}
                <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 flex flex-col gap-2.5">
                    <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                        </svg>
                        <span className="text-sm font-bold text-amber-800">Маңызды ақпарат</span>
                    </div>
                    <div className="flex flex-col gap-2 pl-1">
                        <div className="flex items-start gap-2.5">
                            <span className="mt-0.5 w-5 h-5 rounded-full bg-amber-200 text-amber-700 text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
                            <p className="text-sm text-amber-900 m-0 leading-snug">
                                <span className="font-semibold">Жеткізу мерзімі ұзақ.</span>{' '}
                                Қытайдан тауарлар жеткізу белгілі бір уақытты талап етеді — тапсырыс беру алдында мерзімді ескеріңіз.
                            </p>
                        </div>
                        <div className="flex items-start gap-2.5">
                            <span className="mt-0.5 w-5 h-5 rounded-full bg-amber-200 text-amber-700 text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
                            <p className="text-sm text-amber-900 m-0 leading-snug">
                                <span className="font-semibold">Ең төменгі тапсырыс сомасы — 2 000 000 ₸.</span>{' '}
                                Бұл сомадан төмен тапсырыс қабылданбайды.
                            </p>
                        </div>
                    </div>
                </div>

                {/* ── LEVEL 0: Top categories grid ── */}
                {currentLevel === 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        {chinaCategories.map(cat => (
                            <button
                                key={cat.id}
                                onClick={() => handleTopCategoryClick(cat)}
                                className="group bg-white rounded-2xl overflow-hidden flex flex-col items-center text-center transition-all border-2 border-transparent hover:border-[#147F26]/30 hover:shadow-sm cursor-pointer"
                            >
                                <div className="w-full aspect-square bg-gray-50 flex items-center justify-center p-4">
                                    {cat.image && !imgErr[cat.id] ? (
                                        <img
                                            src={cat.image}
                                            alt={getName(cat)}
                                            className="w-full h-full object-contain"
                                            onError={() => setImgErr(p => ({ ...p, [cat.id]: true }))}
                                        />
                                    ) : (
                                        <span className="text-4xl">{CHINA_FLAG}</span>
                                    )}
                                </div>
                                <div className="px-3 py-2 w-full">
                                    <p className="text-xs font-semibold text-gray-800 leading-tight line-clamp-2 m-0 mb-1">
                                        {getName(cat)}
                                    </p>
                                    {cat.products_count > 0 && (
                                        <span className="inline-block text-[10px] font-medium text-[#147F26] bg-[#147F26]/10 px-2 py-0.5 rounded-full">
                                            {formatCount(cat.products_count)} өнім
                                        </span>
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>
                )}

                {/* ── LEVEL 1: Subcategories ── */}
                {isSubcategoryLevel && (
                    <>
                        {loading ? (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
                            </div>
                        ) : subcategories.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
                                <span className="text-5xl">{CHINA_FLAG}</span>
                                <p className="text-sm">Субкатегориялар табылмады</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                {subcategories.map(cat => (
                                    <button
                                        key={cat.id}
                                        onClick={() => handleSubcategoryClick(cat)}
                                        className={`group bg-white rounded-2xl overflow-hidden flex flex-col items-center text-center transition-all border-2 cursor-pointer ${
                                            activeSubId === cat.id
                                                ? 'border-[#147F26] shadow-md'
                                                : 'border-transparent hover:border-[#147F26]/30 hover:shadow-sm'
                                        }`}
                                    >
                                        <div className="w-full aspect-square bg-gray-50 flex items-center justify-center p-6">
                                            <span className="text-5xl">{CHINA_FLAG}</span>
                                        </div>
                                        <div className="px-3 py-2 w-full">
                                            <p className="text-xs font-semibold text-gray-800 leading-tight line-clamp-2 m-0 mb-1">
                                                {getName(cat)}
                                            </p>
                                            {cat.products_count > 0 && (
                                                <span className="inline-block text-[10px] font-medium text-[#147F26] bg-[#147F26]/10 px-2 py-0.5 rounded-full">
                                                    {formatCount(cat.products_count)} өнім
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </>
                )}

                {/* ── LEVEL 2: Products placeholder ── */}
                {isProductLevel && (
                    <>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
                            {Array.from({ length: 8 }).map((_, i) => (
                                <ProductPlaceholder key={i} index={i} />
                            ))}
                        </div>
                        <div className="rounded-2xl bg-gradient-to-r from-[#147F26]/5 to-[#147F26]/10 border border-[#147F26]/20 p-5 flex items-center gap-4">
                            <span className="text-3xl flex-shrink-0">{CHINA_FLAG}</span>
                            <div>
                                <p className="text-sm font-bold text-gray-800 m-0">Өнімдер жақында қол жетімді болады</p>
                                <p className="text-xs text-gray-500 m-0 mt-0.5">
                                    Қытайдан тікелей жеткізілетін тауарлардың толық каталогы дайындалуда
                                </p>
                            </div>
                        </div>
                    </>
                )}

                {/* Coming soon banner — only on top level */}
                {currentLevel === 0 && (
                    <div className="mt-6 rounded-2xl bg-gradient-to-r from-[#147F26]/5 to-[#147F26]/10 border border-[#147F26]/20 p-5 flex items-center gap-4">
                        <span className="text-3xl flex-shrink-0">{CHINA_FLAG}</span>
                        <div>
                            <p className="text-sm font-bold text-gray-800 m-0">Өнімдер жақында қол жетімді болады</p>
                            <p className="text-xs text-gray-500 m-0 mt-0.5">
                                Қытайдан тікелей жеткізілетін тауарлардың толық каталогы дайындалуда
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ChinaSection;
